import { ReportType } from 'shared/domain/report/types/model/common';
import { isInspectionReportType } from 'shared/domain/report/types/model/reportTypeGuards';
import {
  disableMessageAttachmentsConfig,
  disableMessageSectionConfig,
  disableSectionConfig,
  ReportMessageAttachmentsAvailabilityMap,
  ReportSectionAvailabilityMap,
  ReportSectionId,
  shouldDisableMessageAttachments,
  shouldDisableMessageSection,
  shouldDisableSection,
} from '../model';

export const SYNC_SET_VALUES_OPTIONS = {
  skipFormEdit: true,
  skipValidation: true,
} as const;

type SectionConfigSyncDescriptor = {
  kind: 'sectionConfig';
  sectionId: ReportSectionId;
};

type MessageSectionSyncDescriptor = {
  kind: 'messageSection';
  sectionId:
    | ReportSectionId.events
    | ReportSectionId.incomingEmailMessages
    | ReportSectionId.workflowDecisions;
};

type BooleanSyncDescriptor = {
  kind: 'boolean';
  sectionId: ReportSectionId;
  formKey: string;
};

export type ReportSectionSyncDescriptor =
  | SectionConfigSyncDescriptor
  | MessageSectionSyncDescriptor
  | BooleanSyncDescriptor;

const ISSUE_SECTION_SYNC_DESCRIPTORS: ReportSectionSyncDescriptor[] = [
  { kind: 'sectionConfig', sectionId: ReportSectionId.issueAttachments },
  { kind: 'sectionConfig', sectionId: ReportSectionId.linkedDocumentation },
  { kind: 'messageSection', sectionId: ReportSectionId.events },
  {
    kind: 'messageSection',
    sectionId: ReportSectionId.incomingEmailMessages,
  },
  { kind: 'messageSection', sectionId: ReportSectionId.workflowDecisions },
];

const INSPECTION_SECTION_SYNC_DESCRIPTORS: ReportSectionSyncDescriptor[] = [
  {
    kind: 'boolean',
    sectionId: ReportSectionId.inspectionInfo,
    formKey: 'inspectionInfo',
  },
  { kind: 'boolean', sectionId: ReportSectionId.summary, formKey: 'summary' },
  { kind: 'boolean', sectionId: ReportSectionId.signatures, formKey: 'signatures' },
  {
    kind: 'boolean',
    sectionId: ReportSectionId.issueRegister,
    formKey: 'issueDataSectionEnabled',
  },
  { kind: 'boolean', sectionId: ReportSectionId.generalStandard, formKey: 'generalStandard' },
  {
    kind: 'sectionConfig',
    sectionId: ReportSectionId.generalStandardAttachments,
  },
  {
    kind: 'boolean',
    sectionId: ReportSectionId.sectionDescriptions,
    formKey: 'sectionDescriptions',
  },
  {
    kind: 'sectionConfig',
    sectionId: ReportSectionId.sectionAttachments,
  },
  {
    kind: 'boolean',
    sectionId: ReportSectionId.controlledElementStandard,
    formKey: 'controlledElementStandard',
  },
  {
    kind: 'sectionConfig',
    sectionId: ReportSectionId.controlledElementStandardAttachments,
  },
  {
    kind: 'sectionConfig',
    sectionId: ReportSectionId.controlledElementAttachments,
  },
  { kind: 'sectionConfig', sectionId: ReportSectionId.issueAttachments },
];

export function getReportSectionSyncDescriptors(
  reportType: ReportType
): ReportSectionSyncDescriptor[] {
  return isInspectionReportType(reportType)
    ? INSPECTION_SECTION_SYNC_DESCRIPTORS
    : ISSUE_SECTION_SYNC_DESCRIPTORS;
}

export function buildAvailabilityDependencyKey(
  availability: ReportSectionAvailabilityMap,
  messageAttachmentsAvailability: ReportMessageAttachmentsAvailabilityMap,
  descriptors: ReportSectionSyncDescriptor[]
): string {
  return descriptors
    .map((descriptor) => {
      const section = availability[descriptor.sectionId];
      const base = `${descriptor.sectionId}:${section.available}:${section.unavailableReasonId ?? ''}`;
      if (descriptor.kind !== 'messageSection') {
        return base;
      }
      const attachments =
        messageAttachmentsAvailability[descriptor.sectionId];
      return `${base}:${attachments.available}:${attachments.unavailableReasonId ?? ''}`;
    })
    .join('|');
}

function booleanFlagKey(value: unknown): string {
  return value === true ? '1' : '0';
}

function sectionEnabledKey(value: unknown): string {
  const config = value as { enabled?: boolean } | undefined;
  return booleanFlagKey(config?.enabled);
}

function messageSectionKey(value: unknown): string {
  const config = value as
    | { enabled?: boolean; attachments?: { enabled?: boolean } }
    | undefined;
  return `${booleanFlagKey(config?.enabled)}:${booleanFlagKey(config?.attachments?.enabled)}`;
}

export function buildValuesDependencyKey(
  values: Record<string, unknown>,
  descriptors: ReportSectionSyncDescriptor[]
): string {
  return descriptors
    .map((descriptor) => {
      if (descriptor.kind === 'boolean') {
        return `${descriptor.formKey}:${booleanFlagKey(values[descriptor.formKey])}`;
      }

      if (descriptor.kind === 'sectionConfig') {
        return `${descriptor.sectionId}:${sectionEnabledKey(values[descriptor.sectionId])}`;
      }

      return `${descriptor.sectionId}:${messageSectionKey(values[descriptor.sectionId])}`;
    })
    .join('|');
}

type SyncUnavailableSectionsArgs = {
  availability: ReportSectionAvailabilityMap;
  messageAttachmentsAvailability: ReportMessageAttachmentsAvailabilityMap;
  descriptors: ReportSectionSyncDescriptor[];
  values: Record<string, unknown>;
  setValues: (
    key: string,
    value: unknown,
    options?: typeof SYNC_SET_VALUES_OPTIONS
  ) => void;
};

export function syncUnavailableReportSections({
  availability,
  messageAttachmentsAvailability,
  descriptors,
  values,
  setValues,
}: SyncUnavailableSectionsArgs): void {
  for (const descriptor of descriptors) {
    const sectionAvailability = availability[descriptor.sectionId];

    if (descriptor.kind === 'boolean') {
      if (
        !sectionAvailability.available &&
        values[descriptor.formKey] !== false
      ) {
        setValues(descriptor.formKey, false, SYNC_SET_VALUES_OPTIONS);
      }
      continue;
    }

    const config = values[descriptor.sectionId];
    if (descriptor.kind === 'sectionConfig') {
      if (
        shouldDisableSection(
          config as Parameters<typeof shouldDisableSection>[0],
          sectionAvailability.available
        )
      ) {
        setValues(
          descriptor.sectionId,
          disableSectionConfig(config as Parameters<typeof disableSectionConfig>[0]),
          SYNC_SET_VALUES_OPTIONS
        );
      }
      continue;
    }

    if (
      shouldDisableMessageSection(
        config as Parameters<typeof shouldDisableMessageSection>[0],
        sectionAvailability.available
      )
    ) {
      setValues(
        descriptor.sectionId,
        disableMessageSectionConfig(
          config as Parameters<typeof disableMessageSectionConfig>[0]
        ),
        SYNC_SET_VALUES_OPTIONS
      );
      continue;
    }

    if (
      shouldDisableMessageAttachments(
        config as Parameters<typeof shouldDisableMessageAttachments>[0],
        messageAttachmentsAvailability[descriptor.sectionId].available
      )
    ) {
      setValues(
        descriptor.sectionId,
        disableMessageAttachmentsConfig(
          config as Parameters<typeof disableMessageAttachmentsConfig>[0]
        ),
        SYNC_SET_VALUES_OPTIONS
      );
    }
  }
}
